import { useEffect } from "react";
import clsx from "clsx";

// React Query
import { useQueryClient } from "@tanstack/react-query";

// MUI
import { Dialog, DialogContent, DialogTitle, IconButton, Skeleton, Stack, Typography } from "@mui/material";

// Third Party
import _ from "lodash";

// Hooks
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";
import { useSocialMediaReviewDetailDialog } from "@DataPages/context/customerRelations/SocialMediaReviewDetailedDialogContext";

// Api
import { onCreateReviewResponseDetailsDialogByLocation } from "@DataPages/api/socialMediaReviews/subscriptions";

// Hooks
import { useCRMContext } from "@DataPages/context/crm/CRMContext";

// Helpers
import { or } from "@Common/helpers/sonar";

// Components
import ReviewCard from "./ReviewCard";
import RatingSelect from "@/Layouts/components/RatingSelect";
import AnimatedInputSearch from "@/Layouts/components/Basics/AnimatedInputSearch";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import SmartSelector from "@Layouts/components/InputComponents/SmartSelector";

// Constants
import actionsList from "@Constants/actionsList";

// Assets
import emptyTable from "@/assets/images/commons/empty_table.png";
import CloseIcon from "@mui/icons-material/Close";

// Types
import { IAttributedUser, IReviewDetail } from "@DataPages/types/socialMediaReviews/model";
import { IDialogContent, ReviewDialogSource } from "@DataPages/types/socialMediaReviews/utils";

type ReviewDialogProps = {
	open: boolean;
	isLoading: boolean;
	dialogContent: IDialogContent | null;
	detailedReviews: IReviewDetail[];
	totalReviewAmount: number;
	attributedUsers?: IAttributedUser[];
	/**
	 * @description Prop that indicates which variant should be rendered within the dialog, there are 2 options; **Store** or **Associates**
	 */
	source: ReviewDialogSource;
	// Callbacks
	onClose: () => void;
};

export default function ReviewDetailsDialog(props: Readonly<ReviewDialogProps>) {
	const {
		open,
		isLoading,
		dialogContent,
		source,
		totalReviewAmount,
		detailedReviews,
		attributedUsers,
		// Callbacks
		onClose,
	} = props;
	const queryClient = useQueryClient();
	const { useAuth } = useAuthorization();
	const { filter, onRatingChange, onSearchInputChange, onSelectAssociate } = useSocialMediaReviewDetailDialog();
	const isFiltering = or(Boolean(filter.text), Boolean(filter.users.length), Boolean(filter.ratings.length));

	const { addSubscription, getSubscription } = useSubscriptions();

	const { selection: commissionCycle, locationId } = useCRMContext();
	const { cycleId: commissionCycleId, payPeriodId } = commissionCycle ?? {};

	// Permission to reply to reviews
	const hasReplyReviewPermission = useAuth(actionsList.REPLY_REVIEWS);

	/********************************************* SUBSCRIPTIONS ************************************************/
	const reviewDetailsDialogSubscriptions = getSubscription([
		"createReviewResponseDetailsDialogByLocation",
		locationId,
		commissionCycleId,
		source,
		dialogContent?.columnName,
	]);
	reviewDetailsDialogSubscriptions?.subscriptions?.onCreateReviewResponseDetailsDialogByLocation?.setCallback(() => {
		queryClient.invalidateQueries({
			queryKey: [`${source}-review-details-dialog`, locationId, dialogContent?.columnName],
			type: "all",
			refetchType: "all",
			exact: false,
		});
	});

	useEffect(() => {
		if ([locationId, commissionCycleId, payPeriodId, source, dialogContent?.columnName].every(Boolean)) {
			addSubscription(
				["createReviewResponseDetailsDialogByLocation", locationId, commissionCycleId, source, dialogContent?.columnName],
				[
					{
						context: "onCreateReviewResponseDetailsDialogByLocation",
						query: onCreateReviewResponseDetailsDialogByLocation,
						variables: { locationId, commissionCycleId, source, siteName: dialogContent?.columnName },
					},
				]
			);
		}
	}, [locationId, commissionCycleId, source, dialogContent?.columnName]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			classes={{
				paper: "!rounded-[10px] !shadow-md !min-w-[655px] !h-[570px]",
			}}
			disableEscapeKeyDown
			PaperProps={{
				style: {
					width: "655px",
					height: "570px",
				},
			}}
		>
			<DialogTitle mb={0.5}>
				{isLoading ? (
					<>
						<Skeleton variant="rectangular" className="mt-[4px] !rounded-[6px]" width={250} height={35} />
						<Stack direction="row" justifyContent="space-between" className="mt-[29px]" width="100%">
							<Skeleton variant="rectangular" className="mt-[-2px] !rounded-[6px]" width={150} height={30} />
							<Skeleton variant="circular" className="mr-[30px] mt-[-4px]" width={35} height={35} />
						</Stack>
					</>
				) : (
					<Stack spacing={2}>
						<Typography className={"!font-montserrat !font-medium !text-default !text-2xl"}>
							{(() => {
								if (!dialogContent?.columnName) return "";
								return dialogContent.columnName === "review" ? "Total" : _.capitalize(dialogContent.columnName);
							})()}
							{" Reviews"} ({totalReviewAmount})
						</Typography>

						<Stack
							direction={"row"}
							justifyContent={"space-between"}
							id={"review-filter-actions"}
							component={"form"}
							aria-label="Filter reviews"
						>
							<Stack direction={"row"} spacing={1.5}>
								<RatingSelect onRatingChange={onRatingChange} />
								{source === "Store" && (
									<SmartSelector
										options={attributedUsers ?? []}
										sortBy={"fullName"}
										showBy={"fullName"}
										selectBy={"reputationMgmtUserId"}
										multiple
										clearable
										classes={"!w-[205px]"}
										inputProps={{ placeholder: "Associate(s)" }}
										disablePortal
										tagsVariant={"text"}
										onChangeAction={(value) => onSelectAssociate(value as unknown as IAttributedUser[])}
									/>
								)}
							</Stack>
							<AnimatedInputSearch onInputChange={onSearchInputChange} />
						</Stack>
						{isFiltering && (
							<Typography
								component={"span"}
								display={"inline"}
								alignSelf={"center"}
								className={"!font-sans !text-[14.5px] text-neutral-gray"}
							>{`Showing ${detailedReviews.length} result${detailedReviews.length > 1 ? "s" : ""}`}</Typography>
						)}
					</Stack>
				)}
				<IconButton aria-label="close" onClick={onClose} classes={{ root: "!absolute !top-[4px] !right-[4px] !text-[#B3B3B3]" }}>
					<CloseIcon sx={{ fontSize: "18px" }} />
				</IconButton>
			</DialogTitle>

			<DialogContent
				classes={{
					root: clsx({
						"!pt-[5px] mr-[10px] !pr-[12px]": true,
						"!mb-[30px]": Boolean(detailedReviews?.length),
					}),
				}}
			>
				{isLoading ? (
					<>
						{" "}
						<Skeleton variant="rectangular" className="mb-[20px] !rounded-[6px]" width={590} height={235} />
						<Skeleton variant="rectangular" className={"!rounded-[6px]"} width={590} height={235} />{" "}
					</>
				) : detailedReviews?.length ? (
					<Stack spacing={3} component={"ul"}>
						{detailedReviews.map((reviewDetail) => {
							return (
								<ReviewCard
									key={reviewDetail.reviewUid}
									reviewDetail={reviewDetail}
									locationId={locationId as string}
									hasReplyReviewPermission={hasReplyReviewPermission}
									siteName={dialogContent?.columnName ?? ""}
									source={source}
								/>
							);
						})}
					</Stack>
				) : (
					<EmptyBody
						image={emptyTable}
						imageStyles={{ width: "300px", marginTop: "-80px" }}
						title={"No records for this criteria"}
						bodyHeightClassName="!h-[400px]"
						sx={{
							boxShadow: "none !important",
						}}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
}
